import dayjs from 'dayjs';
import { buildApiUrl } from '../../../utils/createUrl';
import { camelizeKeys } from 'humps';

const AVAILABILITY_KEY_FORMAT = 'YYYY-MM-DD';

/**
 * Calculate the availability fetch window anchored at the given date.
 *
 * - `from` = the anchor day itself.
 * - `to`   = last day of the anchor's month (one-month window). Navigating to a
 *   later month fetches that month on demand, keeping each request ~1 month to
 *   bound the calendar endpoint's cost.
 *
 * Invalid or missing input falls back to "now" (matches the historical
 * behavior of the native-Date implementation).
 *
 * @param {Date|string|null} [visibleDate]
 * @returns {{ from: string, to: string }} YYYY-MM-DD strings, local time.
 */
export const calculateAvailabilityWindow = (visibleDate = null) => {
  const anchor = dayjs(visibleDate);
  const start = anchor.isValid() ? anchor : dayjs();

  return {
    from: start.format(AVAILABILITY_KEY_FORMAT),
    to: start.endOf('month').format(AVAILABILITY_KEY_FORMAT),
  };
};

export const buildAvailabilityApiUrl = ({
  origin,
  destination,
  from,
  to,
  sourceUrl,
  forceHttps = false,
  round = false,
}) => {
  if (!origin || !destination || !from || !to || !sourceUrl) return null;

  const cleanedSourceUrl = sourceUrl.replace(/\/places$/, '');
  const url = buildApiUrl({ sourceUrl: cleanedSourceUrl, path: '/search/calendar', forceHttps });

  url.setQueryParam('origin', origin);
  url.setQueryParam('destination', destination);
  url.setQueryParam('from', from);
  url.setQueryParam('to', to);
  if (round) {
    url.setQueryParam('round', 'true');
  }

  return url.href;
};

export const indexByDate = (rawDays) => {
  if (!Array.isArray(rawDays)) return {};

  return rawDays.reduce((acc, raw) => {
    if (!raw || !raw.date) return acc;

    const camelized = camelizeKeys(raw);
    // The calendar shows the day-level `display_price` (a flat number sibling of
    // `min_price`/`price_level`), not the nested `min_price.pricing`.
    const minPrice =
      typeof camelized.displayPrice === 'number' ? camelized.displayPrice : null;

    acc[camelized.date] = { ...camelized, minPrice };
    return acc;
  }, {});
};
